import { ComponentEntity, stringifyEntityRef } from "@backstage/catalog-model";
import { discoveryApiRef, fetchApiRef, microsoftAuthApiRef, useApi } from "@backstage/core-plugin-api";
import { catalogApiRef, useEntity } from "@backstage/plugin-catalog-react";
import LuxonUtils from '@date-io/luxon';
import { AppEnvironment } from "@internal/open-catalog";
import { Box, Button, Dialog, DialogContent, DialogTitle, IconButton, makeStyles, MenuItem, Snackbar, TextField, Typography } from "@material-ui/core";
import CloseIcon from '@material-ui/icons/Close';
import Alert from "@material-ui/lab/Alert";
import { KeyboardDateTimePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import cronstrue from 'cronstrue';
import React, { useMemo, useState } from "react";
import useAsync from "react-use/esm/useAsync";
import { ScheduleService } from "../../../../../services/ScheduleService";
import { WorkspacesService } from "../../../../../services/WorkspacesService";
import { SystemWorkspace } from "../../../../workspaces/types";
import { MESSAGING_CHANNEL_NAME_ANNOTATION } from "../../../../../components/catalog/useSchedulerTemplateValues";

const useStyles = makeStyles((theme) => ({
    dialogTitle: {
        backgroundColor: '#3b3b3b',
        color: 'white',
        boxShadow: '0px 0px 10px 0px rgb(97, 97, 97)',
    }
}));

export const MaintenanceTask = ({ open, handleClose }: {open: boolean, handleClose: () => void}) => {
    const classes = useStyles();
    const catalogApi = useApi(catalogApiRef);
    const { entity } = useEntity<ComponentEntity>();
    const fetchApi = useApi(fetchApiRef);
    const discoveryApi = useApi(discoveryApiRef);
    const authApi = useApi(microsoftAuthApiRef);

    const [taskCode, setTaskCode] = useState<string>();
    const [taskData, setTaskData] = useState<string>();
    const [isTaskDataInvalid, setTaskDataInvalid] = useState(false);

    const [isRecurrencyInvalid, setRecurrencyInvalid] = useState(false);
    const [recurrency, setRecurrency] = useState<string>('* * * * *');
    const [lastValidRecurrency, setLastValidRecurrency] = useState<string | undefined>('* * * * *');

    const [environment, setEnvironment] = useState<AppEnvironment>('development');
    const [requester, setRequester] = useState('');
    const [workspace, setWorkspace] = useState('');
    const [initialPeriod, setInitialPeriod] = useState<Date | undefined>(new Date());
    const [finalPeriod, setFinalPeriod] = useState<Date | undefined>(() => {
        const now = new Date();
        now.setHours(now.getHours() + 1);
        return now;
    });
    const [executionLimit, setExecutionLimit] = useState<number>(1);
    
    const [openSuccessAlert, setOpenSuccessAlert] = useState(false);
    const [openErrorAlert, setOpenErrorAlert] = useState(false);
    const [errorAlertMsg, setErrorAlertMsg] = useState('');

    const workspacesService = useMemo(
        () => new WorkspacesService(fetchApi, authApi, discoveryApi, catalogApi),
        [fetchApi, authApi, discoveryApi, catalogApi],
    );

    const { value: componentWorkspaces } = useAsync(async () => {
        const systemId = WorkspacesService.getSystemId(entity);
        if (!systemId) {
            return [];
        }
        const items = await workspacesService.getAllWorkspacesBySystem(environment, systemId);
        const workspaces = items.map((item: SystemWorkspace) => WorkspacesService.toWorkspaceOption(item));
        if(workspaces[0]?.tenant)
            setWorkspace(workspaces[0].tenant);
        return workspaces;

    }, [workspacesService, entity, environment]);

    const { value: componentsRequesters } = useAsync(async () => {
        const response = await catalogApi.getEntities({
            filter: { kind: 'Component', ['spec.type']: 'Service', ['relations.partof']: [stringifyEntityRef(entity)] }
        });
        const requesters = response.items.map(item => item.metadata.annotations?.[MESSAGING_CHANNEL_NAME_ANNOTATION]);
        setRequester(requesters[0] || '');
        return requesters;
    }, [catalogApi]);

    const executeMaintenanceTask = async () => {
        if(taskCode && recurrency && workspace && environment && requester &&
            initialPeriod && finalPeriod && executionLimit) {
            const url = await discoveryApi.getBaseUrl('proxy');
            const scheduleService = new ScheduleService(url, fetchApi, environment, authApi);
            const task = { 
                taskCode, 
                taskData: taskData ? JSON.parse(taskData) : undefined,
                recurrency, 
                workspace, 
                requester, 
                initialPeriod: initialPeriod?.toISOString(), 
                finalPeriod: finalPeriod?.toISOString(), 
                executionLimit
            };
            scheduleService.runMaintenanceTask(task)
                .then(() => {
                    setOpenSuccessAlert(true);
                    setTaskCode('');
                    setTaskData('');
                    setRecurrency('* * * * *');
                    setLastValidRecurrency('* * * * *');
                    setWorkspace(componentWorkspaces?.[0]?.tenant || '');
                    setRequester(componentsRequesters && componentsRequesters[0] || '');
                    setEnvironment('development');
                    setInitialPeriod(new Date());
                    const newFinalPeriod = new Date();
                    newFinalPeriod.setHours(newFinalPeriod.getHours() + 1);
                    setFinalPeriod(newFinalPeriod);
                    setExecutionLimit(1);
                })
                .catch((err) => {
                    setErrorAlertMsg(err.message);
                    setOpenErrorAlert(true);
                });
        };
    }

    const shouldDisableFormAction = () => {
        return isRecurrencyInvalid || isTaskDataInvalid || !taskCode || !recurrency || !workspace || !environment || !requester || 
                !initialPeriod || !finalPeriod || !executionLimit;
    } 
 
    const handleRecurrencyChange = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
        const recurrencyValue = event.target.value;
        try {
            cronstrue.toString(recurrencyValue);
            if(recurrencyValue.trim().split(/\s+/).length > 5) {
                setRecurrencyInvalid(true);
                setLastValidRecurrency(undefined);
            } else {
                setRecurrencyInvalid(false);
                setLastValidRecurrency(recurrencyValue);
            }
        } catch (error) {
            setRecurrencyInvalid(true);
            setLastValidRecurrency(undefined);
        }
        setRecurrency(recurrencyValue);
    }
 
    const handleTaskDataChange = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
        const td = event.target.value;
        if(td) {
            try {
                JSON.parse(td);
                setTaskDataInvalid(false);
            } catch (error) {
                setTaskDataInvalid(true);
            }
        } else {
            setTaskDataInvalid(false);
        }
        setTaskData(td);
    }

    return (<>
        <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
            <DialogTitle className={classes.dialogTitle}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="h5">
                        Maintenance Task
                    </Typography>
                    <IconButton edge="start" color="inherit" onClick={handleClose}>
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>
            <DialogContent>
                <Box display="flex">
                    <Box display="flex" flexDirection="column" padding={3} flexGrow={4} gridRowGap={10}>
                        <TextField
                            label="Task Code" value={taskCode} variant="outlined" required
                            onChange={(event) => setTaskCode(event.target.value as any)}
                        />
                        
                        <TextField error={isTaskDataInvalid}
                            label="Task Data" multiline variant="outlined"
                            value={taskData} minRows={15} maxRows={15} fullWidth
                            onChange={handleTaskDataChange} helperText="JSON format"
                        />
                    </Box>
                    <Box flexGrow={1} padding={3}>
                        <Box display="flex" justifyContent="space-between" gridColumnGap={20} marginBottom={3}>
                            <Box flexGrow={1}>
                                <TextField
                                    error={isRecurrencyInvalid} required fullWidth
                                    label="Recurrency" value={recurrency} variant="outlined" 
                                    onChange={handleRecurrencyChange} 
                                /><br/>
                                <Typography variant="caption">
                                    Friendly recurrency: {lastValidRecurrency && cronstrue.toString(lastValidRecurrency)}
                                </Typography>
                            </Box>
                            <Box  flexGrow={1}>
                                <TextField required type="number" fullWidth
                                    label="Execution limit" value={executionLimit} variant="outlined" 
                                    onChange={(event) => setExecutionLimit(event.target.value as any)}
                                />
                            </Box>
                        </Box>
                        
                        <Box display="flex" justifyContent="space-between" marginBottom={5}>
                            <MuiPickersUtilsProvider utils={LuxonUtils}>
                                <KeyboardDateTimePicker disableToolbar variant="dialog"
                                    label="Initial period" value={initialPeriod}
                                    onChange={(event) => setInitialPeriod(event?.toJSDate())}
                                />
                                <KeyboardDateTimePicker disableToolbar variant="dialog"
                                    label="Final period" value={finalPeriod}
                                    onChange={(event) => setFinalPeriod(event?.toJSDate())}
                                />
                            </MuiPickersUtilsProvider>
                        </Box>
                        <Box display="flex" flexDirection="column" gridRowGap={10} marginBottom={5}>
                            <TextField select required
                                label="Requester" variant="outlined" value={requester}
                                onChange={(event) => setRequester(event.target.value)}
                            >
                                {componentsRequesters?.map((requesterComponent) => (
                                    <MenuItem key={requesterComponent} value={requesterComponent}>
                                        {requesterComponent}
                                    </MenuItem>
                                ))}
                            </TextField>
                            <TextField select required
                                label="Environment" value={environment} variant="outlined"
                                onChange={(event) => setEnvironment(event.target.value as any)}
                            >
                                {['development', 'staging', 'production'].map((env) => (
                                    <MenuItem key={env} value={env}>
                                        {env}
                                    </MenuItem>
                                ))}
                            </TextField>
                            <TextField select required
                                label="Workspace" variant="outlined" value={workspace}
                                onChange={(event) => setWorkspace(event.target.value)}
                            >
                                {componentWorkspaces?.map((workspaceComponent) => (
                                    <MenuItem key={workspaceComponent.tenant} value={workspaceComponent.tenant}>
                                        {workspaceComponent.name}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Box>
                    </Box>
                </Box>
                <Box display="flex" justifyContent="end" pb={3} pr={3}>
                    <Button 
                        variant="contained" color="secondary" size="large"
                        onClick={executeMaintenanceTask}
                        disabled={shouldDisableFormAction()}>
                        Run maintenance task
                    </Button>
                </Box>
            </DialogContent>
            <Snackbar open={openSuccessAlert} autoHideDuration={5000} onClose={() => setOpenSuccessAlert(false)}>
                <Alert onClose={() => setOpenSuccessAlert(false)} severity="success">
                    Maintenance task scheduled successfully
                </Alert>
            </Snackbar>
            <Snackbar open={openErrorAlert} onClose={() => setOpenErrorAlert(false)}>
                <Alert onClose={() => setOpenErrorAlert(false)} severity="error">
                    Problem scheduling a maintenance task{errorAlertMsg && ':'}<br/>
                    {errorAlertMsg}
                </Alert>
            </Snackbar>
        </Dialog>
    </>);
}